import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vcollectEnumeratorsApi } from '../../api/vcollect-enumerator';
import { vcollectProjectsApi } from '../../api/vcollect';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { EmployeePicker } from '../../components/EmployeePicker';
import { useToast } from '../../components/Toast';

export function TeamsPage() {
  const [searchParams] = useSearchParams();
  const preselectedProjectId = searchParams.get('vcollectProjectId');
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null);
  const [addingMemberTo, setAddingMemberTo] = useState<number | null>(null);
  const [selectedEnumeratorId, setSelectedEnumeratorId] = useState('');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset, setValue } = useForm<{ name: string; vcollectProjectId?: number; supervisorEmployeeId?: number }>();

  useEffect(() => {
    if (preselectedProjectId) setValue('vcollectProjectId', Number(preselectedProjectId));
  }, [preselectedProjectId, setValue]);

  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['vcollect-teams', preselectedProjectId],
    queryFn: () => vcollectEnumeratorsApi.listTeams({ vcollectProjectId: preselectedProjectId ? Number(preselectedProjectId) : undefined }),
  });
  const teams = teamsData?.data ?? [];
  const { data: vcollectProjectsData } = useQuery({ queryKey: ['vcollect-projects'], queryFn: () => vcollectProjectsApi.list(), enabled: !preselectedProjectId });
  const vcollectProjects = vcollectProjectsData?.data ?? [];
  const { data: enumeratorsData } = useQuery({ queryKey: ['vcollect-enumerators'], queryFn: () => vcollectEnumeratorsApi.list() });
  const enumerators = enumeratorsData?.data ?? [];
  const { data: membersData } = useQuery({ queryKey: ['vcollect-team-members', expandedTeamId], queryFn: () => vcollectEnumeratorsApi.listTeamMembers(expandedTeamId!), enabled: !!expandedTeamId });
  const members = membersData?.data ?? [];

  const createTeamMutation = useMutation({
    mutationFn: (input: { name: string; vcollectProjectId?: number; supervisorEmployeeId?: number }) => vcollectEnumeratorsApi.createTeam(input),
    onSuccess: () => { showSuccess('Team created.'); queryClient.invalidateQueries({ queryKey: ['vcollect-teams'] }); setShowCreateForm(false); reset(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const addMemberMutation = useMutation({
    mutationFn: () => vcollectEnumeratorsApi.addTeamMember(addingMemberTo!, Number(selectedEnumeratorId)),
    onSuccess: () => {
      showSuccess('Member added.');
      queryClient.invalidateQueries({ queryKey: ['vcollect-team-members', addingMemberTo] });
      queryClient.invalidateQueries({ queryKey: ['vcollect-teams'] });
      setAddingMemberTo(null);
      setSelectedEnumeratorId('');
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({ teamId, enumeratorId }: { teamId: number; enumeratorId: number }) => vcollectEnumeratorsApi.removeTeamMember(teamId, enumeratorId),
    onSuccess: (_r, { teamId }) => {
      showSuccess('Member removed.');
      queryClient.invalidateQueries({ queryKey: ['vcollect-team-members', teamId] });
      queryClient.invalidateQueries({ queryKey: ['vcollect-teams'] });
    },
    onError: (err: ApiError) => showError(err.message),
  });

  return (
    <div className="page">
      {preselectedProjectId ? (
        <a onClick={() => navigate(`/vcollect/projects/${preselectedProjectId}`)} className="table-link">← Back to Project</a>
      ) : null}
      <PageHeader title="VCollect — Field Teams" description="Groups of enumerators sharing a supervisor, working the same forms and area." />

      <SectionHeader title="Teams" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Team'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createTeamMutation.mutate(d))}>
          <label>Team Name <input placeholder="e.g. Team A - Potiskum" {...register('name', { required: true })} /></label>
          {preselectedProjectId ? (
            <input type="hidden" {...register('vcollectProjectId', { valueAsNumber: true })} />
          ) : (
            <label>Data Collection Project
              <select {...register('vcollectProjectId', { valueAsNumber: true })}>
                <option value="">No project (global team)</option>
                {vcollectProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}
          <label>Supervisor <EmployeePicker {...register('supervisorEmployeeId', { valueAsNumber: true })} /></label>
          <button type="submit" disabled={createTeamMutation.isPending}>Create Team</button>
        </form>
      )}

      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <div>
          {teams.map((t) => (
            <div key={t.id} style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 4, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <b>{t.name}</b> <span className="muted">— {t.supervisor_name ?? 'No supervisor set'} — {t.member_count} member(s)</span>
                </div>
                <button className="btn-secondary" onClick={() => setExpandedTeamId(expandedTeamId === t.id ? null : t.id)}>
                  {expandedTeamId === t.id ? 'Hide Members' : 'Show Members'}
                </button>
              </div>
              {expandedTeamId === t.id && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--color-border)' }}>
                  <ul>
                    {members.map((m) => (
                      <li key={m.id} style={{ marginBottom: 4 }}>
                        {m.enumerator_name} ({m.enumerator_reference})
                        <button className="btn-secondary" style={{ marginLeft: 8 }} onClick={() => removeMemberMutation.mutate({ teamId: t.id, enumeratorId: m.enumerator_id })}>Remove</button>
                      </li>
                    ))}
                    {members.length === 0 && <li className="muted">No members yet.</li>}
                  </ul>
                  {addingMemberTo === t.id ? (
                    <div className="inline-form">
                      <select value={selectedEnumeratorId} onChange={(e) => setSelectedEnumeratorId(e.target.value)}>
                        <option value="">Select enumerator…</option>
                        {enumerators.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                      <button onClick={() => addMemberMutation.mutate()} disabled={!selectedEnumeratorId || addMemberMutation.isPending}>Add</button>
                      <button className="btn-secondary" onClick={() => setAddingMemberTo(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="btn-secondary" onClick={() => setAddingMemberTo(t.id)}>+ Add Member</button>
                  )}
                </div>
              )}
            </div>
          ))}
          {teams.length === 0 && <p className="muted">No teams yet.</p>}
        </div>
      )}
    </div>
  );
}
